import React, { useState, useEffect } from "react";
import { Spin, Card, Empty } from "antd";
import { useDispatch } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { getApi } from "../../redux/rest/restSlice";
import { ME } from "../../constants/actionTypes";
import { SECTION_GROUPS } from "../../constants/dashboardSections";
import {
  useActiveSection,
  useAllowedSections,
} from "../../components/AppLayout/useDashboardSections";
import PageHeader from "../../components/PageHeader";

/**
 * Shell shared by every dashboard section. Each section has its own route, so
 * the sidebar, the browser history and shared links all stay in step; this
 * component only supplies the page chrome and guards the section behind its
 * permissions.
 */
const DashboardScreen = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const allowedSections = useAllowedSections();
  const activeSection = useActiveSection();

  /* 🔐 Validate session on load */
  useEffect(() => {
    dispatch(getApi({ endpoint: ME })).finally(() => setLoading(false));
  }, [dispatch]);

  /* 🔹 Loading State */
  if (loading) {
    return (
      <div className="page-loading">
        <Spin size="large" />
      </div>
    );
  }

  /* 🔹 No Access State */
  if (!allowedSections.length) {
    return (
      <Card bordered={false} className="section-card">
        <Empty description="You do not have access to any dashboard modules" />
      </Card>
    );
  }

  /* 🔹 /dashboard, or a section this user may not open, lands on the first
        section they are allowed to see */
  const isAllowed =
    activeSection && allowedSections.some((s) => s.key === activeSection.key);

  if (!isAllowed) {
    return <Navigate to={allowedSections[0].path} replace />;
  }

  const activeGroup = SECTION_GROUPS.find(
    (group) => group.key === activeSection.group
  );

  return (
    <div className="page">
      {/* 🔹 Page Header */}
      <PageHeader eyebrow={activeGroup?.label} title={activeSection.label} />

      {/* 🔹 The section matched by the current route */}
      <Card bordered={false} className="section-card">
        <Outlet />
      </Card>
    </div>
  );
};

export default DashboardScreen;
